import { useEffect, useState } from "react";
import { Loader2, Save, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/ui/rich-editor";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import {
  useCreateArticle,
  useUpdateArticle,
  type ArticleInput,
} from "@/hooks/useArticleMutations";
import { type Visibility } from "@/hooks/useDocumentMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatBytes } from "@/lib/format";
import type { ArticleRow, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

const CATEGORY_OPTIONS = [
  { value: "policy", label: "Policy" },
  { value: "plan",   label: "Plan" },
  { value: "report", label: "Report" },
  { value: "other",  label: "Other" },
];

interface ArticleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  article?: ArticleRow | null;
}

/** Create + edit form for Articles. Same shape as DocumentFormDialog
 *  but with a content textarea instead of file upload, and image
 *  optional on both create and edit. */
export function ArticleFormDialog({ open, onOpenChange, article }: ArticleFormDialogProps) {
  const isEdit = !!article;
  const { data: user } = useCurrentUser();
  const create = useCreateArticle();
  const update = useUpdateArticle();
  const mutation = isEdit ? update : create;

  const [title,      setTitle]      = useState("");
  const [content,    setContent]    = useState("");
  const [category,   setCategory]   = useState("policy");
  const [visibility, setVisibility] = useState<Visibility>("staff");
  const [image,      setImage]      = useState<File | null>(null);
  const [progress,   setProgress]   = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    if (article) {
      setTitle(article.title);
      setContent("");        // list serializer omits content; user can re-type
      setCategory(article.category || "policy");
      setVisibility(visibilityFromState(article.status, article.requested_visibility));
    } else {
      setTitle(""); setContent(""); setCategory("policy"); setVisibility("staff");
    }
    setImage(null);
    setProgress(null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article?.id]);

  const canSubmit = title.trim().length > 0
    && (isEdit || content.trim().length > 0)
    && (isEdit || image !== null)         // cover image required on create
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    // Only show the progress bar when there's a file going up.
    const wantsProgress = !!image;
    if (wantsProgress) setProgress(0);
    const input: ArticleInput = {
      title:      title.trim(),
      content:    content.trim() || undefined,
      category,
      visibility,
      image:      image || undefined,
      onProgress: wantsProgress ? setProgress : undefined,
    };
    const handlers = {
      onSuccess: () => onOpenChange(false),
      onSettled: () => setProgress(null),
    };
    if (isEdit && article) {
      update.mutate({ slug: article.slug, input }, handlers);
    } else {
      create.mutate(input, handlers);
    }
  }

  const err = mutation.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit “${article?.title}”` : "New article"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update fields below. Leave content blank to keep the existing body."
              : "Write a brief, op-ed, or long-form post. Submit for review or publish directly per your role."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="afd-title">Title <span className="text-destructive">*</span></Label>
            <Input
              id="afd-title" required maxLength={255}
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="A concise, descriptive headline"
            />
            {!isEdit && (
              <p className="text-xs text-muted-foreground">
                A URL slug is generated from the title automatically.
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="afd-category">Category</Label>
              <select
                id="afd-category"
                value={category} onChange={(e) => setCategory(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="afd-image">
                Cover image {!isEdit && <span className="text-destructive">*</span>}
                {isEdit && <span className="text-xs text-muted-foreground"> — optional</span>}
              </Label>
              <ImagePicker image={image} onChange={setImage} />
              <UploadProgressBar progress={progress} totalBytes={image?.size} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="afd-content">
              Body {!isEdit && <span className="text-destructive">*</span>}
            </Label>
            <RichEditor
              value={content}
              onChange={setContent}
              disabled={mutation.isPending}
              minHeightClass="min-h-[220px]"
              placeholder={isEdit
                ? "Leave blank to keep the existing body."
                : "Write the article body — use the toolbar for headings, lists, links."}
            />
          </div>

          <div className="space-y-2">
            <Label>Who can see this?</Label>
            <div className="grid gap-2">
              <VisibilityOption
                value="private" current={visibility} onChange={setVisibility}
                label="Only me"
                hint="Draft, visible only to you."
              />
              <VisibilityOption
                value="staff" current={visibility} onChange={setVisibility}
                label="Staff portal"
                hint={user?.is_director_level
                  ? "Visible to every signed-in colleague immediately."
                  : "Visible to staff after your director approves it."}
              />
              <VisibilityOption
                value="public" current={visibility} onChange={setVisibility}
                label="Public portal"
                hint={
                  user?.is_ceo_level
                    ? "Live on the public site immediately."
                    : user?.is_director_level
                      ? "Goes to the CEO for final sign-off before publishing."
                      : "Goes through director, then CEO sign-off, before publishing."
                }
              />
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="ghost"
              onClick={() => onOpenChange(false)} disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {progress !== null && progress >= 0 && progress < 100
                      ? `Uploading ${progress}%`
                      : isEdit ? "Saving…" : "Publishing…"}
                  </>
                )
                : isEdit
                  ? <><Save className="mr-2 h-4 w-4" />Save changes</>
                  : <><Upload className="mr-2 h-4 w-4" />Create article</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function visibilityFromState(state: WorkflowState, requested: WorkflowState | null): Visibility {
  if (state === "owner_only")   return "private";
  if (state === "private_only") return "staff";
  if (state === "published")    return "public";
  const target = requested ?? "published";
  return target === "private_only" ? "staff" : "public";
}

function VisibilityOption({
  value, current, onChange, label, hint,
}: {
  value: Visibility; current: Visibility;
  onChange: (v: Visibility) => void;
  label: string; hint: string;
}) {
  const checked = current === value;
  return (
    <label className={cn(
      "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
      checked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-accent/30",
    )}>
      <input
        type="radio" name="afd-visibility" value={value} checked={checked}
        onChange={() => onChange(value)}
        className="mt-1 h-4 w-4 accent-primary"
      />
      <div className="flex-1 text-sm">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </label>
  );
}

function ImagePicker({
  image, onChange,
}: { image: File | null; onChange: (f: File | null) => void }) {
  return image ? (
    <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3 text-sm">
      <div className="min-w-0">
        <div className="truncate font-medium">{image.name}</div>
        <div className="text-xs text-muted-foreground">{formatBytes(image.size)}</div>
      </div>
      <Button type="button" variant="ghost" size="icon" onClick={() => onChange(null)}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  ) : (
    <label
      htmlFor="afd-image-input"
      className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/40"
    >
      <Upload className="h-4 w-4" />
      <span>Choose image</span>
      <input
        id="afd-image-input" type="file" accept="image/*"
        className="sr-only"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
